import React, { useState, useEffect } from "react";
import Select from "react-select";
import { debounce } from "lodash";
import { getSavedSearch, getSearchLogDetails } from "api/search.api";
import SearchFilterDropdown from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { addEducationFields, addExcludeLocations, addIndustry, addJobTitle, addKeyword, addLocations, addMaxExp, addMinExp, addName, addSkills, addUniversity, setEverWorked, setExactJobTitleMatch, setExcludeSubsidiaries, setIncludeSubsidiaries, setTitleWithPreviousJob, setWorkOnPreviousCompany } from "store/slices/searcSlice";
import { useDispatch } from "react-redux";

interface SavedSearchDetails {
  id: string;
  name: string; // Name for the saved search
}

interface SavedSearchSelectProps {
  onSelect?: (selectedOption: SavedSearchDetails | null) => void;
  styles?: any; // Optional: Allow custom styles to be passed
}

const SavedSearchSelect: React.FC<SavedSearchSelectProps> = ({ onSelect, styles }) => {
  const [options, setOptions] = useState<SavedSearchDetails[]>([]);
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const dispatch = useDispatch();
  // Fetch saved searches based on the current page and search term
  const fetchSavedSearch = (currentPage: number, search: string) => {
    if (!hasMore) return;

    setLoading(true);
    getSavedSearch(currentPage)
      .then((result: any) => {
        if (currentPage === 1) {
          setOptions(result.results);
        } else {
          setOptions((prevOptions) => [...prevOptions, ...result.results]);
        }
        setHasMore(!!result.next);
        setPage(currentPage + 1);
      })
      .catch((error) => {
        console.error("Error fetching saved searches:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const debouncedFetchSavedSearch = debounce(fetchSavedSearch, 300);

  useEffect(() => {
    fetchSavedSearch(page, searchTerm);
  }, []);

  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    setPage(1);
    debouncedFetchSavedSearch(1, inputValue);
  };

  const handleMenuScrollToBottom = () => {
    if (!loading && hasMore) {
      debouncedFetchSavedSearch(page, searchTerm);
    }
  };

  const handleChange = async (selected: any) => {
    const lastSelectedValue = selected[selected.length - 1];
    try {
      setLoading(true);
      const res = await getSearchLogDetails(lastSelectedValue);
      dispatch(addName(res.search_title));
      dispatch(addJobTitle(res?.search_title));
      if (res.skills) {
        dispatch(addSkills(res.skills));
      }
      dispatch(setEverWorked(res?.search_wizard_config?.ever_worked ? res?.search_wizard_config?.ever_worked : false));
      dispatch(setExactJobTitleMatch(res?.exact_job_title_match));
      dispatch(setTitleWithPreviousJob(res?.allow_previous_job_titles));
      dispatch(setIncludeSubsidiaries(res?.include_subsidiaries));
      dispatch(setExcludeSubsidiaries(res?.exclude_subsidiaries));
      dispatch(setWorkOnPreviousCompany(res?.worked_previously_at_companies));
      dispatch(addKeyword(res?.search_wizard_config?.keywords));
      dispatch(addIndustry(res?.search_wizard_config?.industries));
      dispatch(addUniversity(res?.search_wizard_config?.universities));
      dispatch(addEducationFields(res?.search_wizard_config?.education_fields));
      dispatch(addLocations(res?.search_wizard_config?.locations));
      dispatch(addExcludeLocations(res?.search_wizard_config?.locations_excluded));
      if (res.min_experience) {
        dispatch(addMinExp(res.min_experience));
      }
      if (res.max_experience) {
        dispatch(addMaxExp(res.max_experience));
      }

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    setSelectedSearchId(lastSelectedValue);
  };



  return (

    <SearchFilterDropdown
      options={options.map((option: any) => ({ value: option.id, label: option.job.title }))}
      onChange={(values: any) => {
        handleChange(values);
      }}
      defaultValue={selectedSearchId ? selectedSearchId : undefined}
      placeholder="Select Saved Search..."
      onInputChange={handleInputChange}
      loading={loading}
    />
    // <Select
    //   options={options}
    //   isLoading={loading}
    //   placeholder="Select Saved Search..."
    //   getOptionLabel={(option) => option.name} // Use the name as the label
    //   getOptionValue={(option) => option.id} // Use the id as the value
    //   onInputChange={handleInputChange} // Trigger search on input change
    //   onChange={(selectedOption) =>
    //     onSelect && onSelect(selectedOption as SavedSearchDetails | null)
    //   }
    //   styles={styles} // Apply custom styles if provided
    //   onMenuScrollToBottom={handleMenuScrollToBottom} // Trigger loading more saved searches on scroll
    //   className="w-3/5 select-saved-search focus:border-border-theme text-quaternary rounded-lg"
    // />
  );
};

export default SavedSearchSelect;